import React, { useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { Link } from "react-router-dom";

import styled, { css } from "styled-components";

import Icon from "components/atoms/Icon";
import Image from "components/atoms/Image";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Button from "components/atoms/button/Button";

import AbstractModal from "components/page/modal/AbstractModal";
import AdminChangeUserModal from "components/page/modal/AdminChangeUserModal";
import PageHeaderDropDownMenu from "components/templates/header/PageHeaderDropDownMenu";
import PageHeaderNavItem from "components/templates/header/PageHeaderNavItem";

import { useUser } from "hooks/queries/useUser";

import Constants from "utils/constants";
import { useDevice } from "utils/device";
import { logout } from "utils/request/auth";

const Container = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 8px;
    width: 100%;
    height: ${Constants.MOBILE_HEADER_HEIGHT}px;
    background-color: ${(props) => (props.isIndexPage ? "rgba(255, 255, 255, 0.8)" : "var(--color-White)")};
    border-bottom: ${(props) => (props.isIndexPage ? "transparent" : "solid 1px var(--color-Line)")};
    z-index: 100;

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            padding: 0 16px;
            min-width: 768px;
            height: ${Constants.TABLET_OR_PC_HEADER_HEIGHT}px;
        `};

    ${(props) =>
        props.device.isPC &&
        css`
            padding: 0 16px;
        `};
`;

const LogoWrap = styled.div`
    transform: scale(0.8);

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            transform: scale(1);
        `};
`;

const HamburgerButtonWrap = styled.div`
    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            display: none;
        `};
`;

const NavSection = styled.div`
    display: none;

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            display: flex;
            align-items: center;
            height: 36px;

            a {
                height: 100%;
            }
        `};
`;

const UserIdWrap = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 8px 0 20px;
    height: 32px;
    border-radius: 6px;
    transition: all 80ms ease-in;
    cursor: pointer;
`;

const MobileBlankBox = styled.div`
    width: 24px;

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            display: none;
        `};
`;

const GNBBackground = styled.div`
    position: fixed;
    top: ${Constants.MOBILE_HEADER_HEIGHT}px;
    left: 0;
    width: 100vw;
    height: calc(100vh - ${Constants.MOBILE_HEADER_HEIGHT}px);
    background-color: red;
    visibility: ${({ menuVisible }) => (menuVisible ? "visible" : "hidden")};
    opacity: ${({ menuVisible }) => (menuVisible ? "1" : "0")};
    transition: all 120ms ease-in;

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            display: none;
        `};
`;

const GNBSideMenuContainer = styled.div`
    width: 100vw;
    height: 100%;
    background-color: var(--color-White);
    transition: all 120ms ease-in-out;
`;

const MenuItem = styled.div`
    width: 100%;
    height: 56px;
    background-color: var(--color-White);
`;

const MobileUserMenuWrap = styled.div`
    width: 100%;
`;

const MobileUserIDWrap = styled.div`
    display: flex;
    justify-content: center;
    padding: 16px 32px;
    width: 100%;
    border-top: solid 2px var(--color-Line);
`;

const ProfileInitialButton = styled.div`
    width: 32px;
    height: 32px;
    border: ${({ menuOpen }) => (menuOpen ? "2px solid var(--color-Button1)" : "1px solid var(--color-Line)")};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 1.4rem;
    font-weight: 600;
    text-transform: uppercase;
    transition: all 120ms ease-in-out;

    background-color: var(--color-ButtonHover4);
    color: black;

    user-select: none;
    pointer-events: none;
`;

const MobileButtonStyle = {
    padding: "0 16px",
    height: "56px",
    bgColor: "transparent",
    hoverBgColor: "var(--color-ButtonHover4)",
    borderRadius: "0",
    fontSize: "regular",
    fontWeight: "600",
    fontColor: "var(--color-Black)",
};

function PageHeader({ ...props }) {
    const device = useDevice();

    const [menuOpen, setMenuOpen] = useState(false);
    const [menuVisible, setMenuVisible] = useState(false);
    const [showAdminChangeUserModal, setShowAdminChangeUserModal] = useState(false);

    const dropdownRef = useRef(null);

    useEffect(() => {
        // 메뉴 바깥 클릭 시 드롭다운 닫기 처리
        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    const [user, setUser] = useState(null);
    const userQuery = useUser();

    useEffect(() => {
        if (userQuery.data) {
            setUser(userQuery.data);
        }
    }, [userQuery.data]);

    const logoutMutation = useMutation(logout, {
        onSuccess: (data) => {
            localStorage.removeItem("token");
            sessionStorage.clear();
            window.location = "/";
        },
        onError: (e) => {
            Alert("warn", "에러", "서버에 오류가 발생하였습니다.");
        },
    });

    const handleOutsideClick = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setMenuOpen(false);
        }
        // if ()
    };

    const toggleMenuVisibility = () => {
        setMenuVisible(!menuVisible);
    };

    const handleCustomerSupportClick = () => {
        window.open("https://support.preppaper.com/", "_blank");
    };

    return (
        <Container device={device} isIndexPage={props.isIndexPage}>
            <HamburgerButtonWrap device={device}>
                <Button
                    onClick={toggleMenuVisibility}
                    onlyIcon
                    bgColor={"transparent"}
                    hoverBgColor={"var(--color-ButtonHover4)"}
                >
                    <Icon name={"hamburger"} />
                </Button>
            </HamburgerButtonWrap>
            <LogoWrap device={device}>
                <Link as={Link} to={"/"} reloadDocument>
                    <Image name={"headerLogo"} />
                </Link>
            </LogoWrap>
            <NavSection device={device}>
                <>
                    <PageHeaderNavItem linkAddress={"/plan"} menuName={"Pricing"} />
                    <PageHeaderNavItem menuName={"Support"} onClick={handleCustomerSupportClick} />
                    {user?.is_authenticated ? (
                        <>
                            <UserIdWrap ref={dropdownRef} onClick={(e) => setMenuOpen(!menuOpen)} menuOpen={menuOpen}>
                                <Tooltip message={user.email} placement="bottom">
                                    <ProfileInitialButton menuOpen={menuOpen}>
                                        {user.email.trim()?.[0] || "?"}
                                    </ProfileInitialButton>
                                </Tooltip>

                                <PageHeaderDropDownMenu
                                    isVisible={menuOpen}
                                    isStaff={user.is_staff || false}
                                    logoutClick={(e) => logoutMutation.mutate()}
                                    setShowAdminChangeUserModal={setShowAdminChangeUserModal}
                                />
                            </UserIdWrap>
                        </>
                    ) : (
                        <PageHeaderNavItem linkAddress={"/login"} menuName={"Sign In"} />
                    )}
                </>
            </NavSection>
            <MobileBlankBox device={device} />
            <GNBBackground device={device} menuVisible={menuVisible}>
                <GNBSideMenuContainer>
                    <Link as={Link} to={"/plan"}>
                        <Button onlyText {...MobileButtonStyle} buttonText={"Pricing"} />
                    </Link>
                    <MenuItem onClick={handleCustomerSupportClick}>
                        <Button onlyText {...MobileButtonStyle} buttonText={"Support"} />
                    </MenuItem>
                    {user?.is_authenticated ? (
                        <>
                            <MobileUserMenuWrap>
                                <Link as={Link} to={"/account"}>
                                    <Button onlyText {...MobileButtonStyle} buttonText={"Account"} />
                                </Link>
                                <Link as={Link} to={"/settings"}>
                                    <Button onlyText {...MobileButtonStyle} buttonText={"Settings"} />
                                </Link>
                                <Button
                                    onlyText
                                    {...MobileButtonStyle}
                                    buttonText={"Sign Out"}
                                    onClick={(e) => logoutMutation.mutate()}
                                />
                            </MobileUserMenuWrap>
                            <MobileUserIDWrap>
                                <Button onlyText {...MobileButtonStyle} buttonText={user.email} />
                            </MobileUserIDWrap>
                        </>
                    ) : (
                        <Link as={Link} to={"/login"}>
                            <Button onlyText {...MobileButtonStyle} buttonText={"Sign In"} />
                        </Link>
                    )}
                </GNBSideMenuContainer>
            </GNBBackground>
            {showAdminChangeUserModal && (
                <AbstractModal modalTitle="계정 전환" width={480} exitModal={(e) => setShowAdminChangeUserModal(false)}>
                    <AdminChangeUserModal exitModal={() => setShowAdminChangeUserModal(false)} />
                </AbstractModal>
            )}
        </Container>
    );
}

export default PageHeader;
